/**
 * RequestAccessForm Component
 * Request access form using only Ant Design components
 */

import React from "react";

import {
  ArrowRightOutlined,
  BankOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Alert, Button, Form, Input, Typography, Row, Col, Space } from "antd";

import type { RequestAccessFormData } from "../types/auth.types";

const { Text } = Typography;
const { TextArea } = Input;

interface RequestAccessFormProps {
  onSubmit: (data: RequestAccessFormData) => Promise<void>;
  error?: string | null;
  isLoading?: boolean;
}

export const RequestAccessForm: React.FC<RequestAccessFormProps> = ({
  onSubmit,
  error,
  isLoading,
}) => {
  const [form] = Form.useForm();

  const handleFinish = async (values: RequestAccessFormData) => {
    await onSubmit(values);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {error && <Alert message={error} type="error" showIcon closable />}

      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        size="large"
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          challenge: "",
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstName"
              label={
                <Space>
                  <UserOutlined />
                  <Text>First name</Text>
                </Space>
              }
              rules={[
                { required: true, message: "First name is required" },
                { min: 2, message: "First name must be at least 2 characters" },
              ]}
            >
              <Input type="text" placeholder="John" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastName"
              label="Last name"
              rules={[
                { required: true, message: "Last name is required" },
                { min: 2, message: "Last name must be at least 2 characters" },
              ]}
            >
              <Input type="text" placeholder="Doe" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="email"
          label={
            <Space>
              <MailOutlined />
              <Text>Work email</Text>
            </Space>
          }
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input type="email" placeholder="john@company.com" />
        </Form.Item>

        <Form.Item
          name="company"
          label={
            <Space>
              <BankOutlined />
              <Text>Company</Text>
              <Text type="secondary">(optional)</Text>
            </Space>
          }
        >
          <Input type="text" placeholder="Acme Inc." />
        </Form.Item>

        <Form.Item
          name="challenge"
          label="What's your biggest challenge with product validation?"
          rules={[
            { required: true, message: "Please tell us about your challenges" },
            {
              min: 10,
              message: "Please provide more details (at least 10 characters)",
            },
          ]}
        >
          <TextArea
            placeholder="Getting user feedback, finding the right audience, long development cycles..."
            rows={3}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            block
            icon={<ArrowRightOutlined />}
          >
            Request Access
          </Button>
        </Form.Item>
      </Form>

      <Text type="secondary" style={{ textAlign: "center", display: "block" }}>
        Already have access?{" "}
        <Text type="secondary" style={{ color: "#BB8800", cursor: "pointer" }}>
          Sign in
        </Text>
      </Text>
    </Space>
  );
};

export default RequestAccessForm;
